import type { CSSProperties } from "react";

/**
 * Branding helpers (BR-3). Guest share pages store an accent as a hex color,
 * but the theme tokens are HSL triplets consumed as `hsl(var(--primary))`. So
 * we convert hex → "H S% L%" and override the relevant CSS vars on a wrapper.
 */

/** "#7c5cff" → "252 100% 68%" (H S% L%), or null if not a valid 6-digit hex. */
export function hexToHslTriplet(hex: string | null | undefined): string | null {
  if (!hex) return null;
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let sat = 0;
  const d = max - min;
  if (d !== 0) {
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

/** Perceived-luminance test so text on the accent stays readable. */
function accentIsLight(hex: string): boolean {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return false;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}

/**
 * CSS-var overrides for a guest page root. Returns undefined when there's no
 * valid accent, so the page keeps the default Editlock theme.
 */
export function brandAccentStyle(accent: string | null | undefined): CSSProperties | undefined {
  const triplet = hexToHslTriplet(accent);
  if (!triplet || !accent) return undefined;
  const fg = accentIsLight(accent) ? "240 10% 12%" : "0 0% 100%";
  const vars: Record<string, string> = {
    "--primary": triplet,
    "--primary-foreground": fg,
    "--brand": triplet,
    "--brand-foreground": fg,
    "--ring": triplet,
  };
  return vars as CSSProperties;
}
